import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Save, Leaf, Drumstick, Image as ImageIcon } from 'lucide-react';
import { getMenuItems, createMenuItem, updateMenuItem, deleteMenuItem, updateMenuItemAvailability } from '../../services/api';
import { MenuItem } from '../../types';
import { useToast } from '../../context/ToastContext';
import ConfirmationModal from '../common/ConfirmationModal';
import './MenuManagement.css';

const MenuManagement: React.FC = () => {
  const { showToast } = useToast();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [deleteModal, setDeleteModal] = useState<{isOpen: boolean, itemId: string | null}>({
    isOpen: false,
    itemId: null
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    image: '',
    isVeg: true,
    isAvailable: true,
    preparationTime: '',
    tags: '',
  });

  useEffect(() => {
    fetchMenuItems();
  }, []);

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      const response = await getMenuItems();
      setMenuItems(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch menu items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const menuItem = {
      id: editingItem?.id || `item_${Date.now()}`,
      name: formData.name,
      description: formData.description,
      category: formData.category,
      price: parseFloat(formData.price),
      image: formData.image,
      isVeg: formData.isVeg,
      isAvailable: formData.isAvailable,
      preparationTime: parseInt(formData.preparationTime),
      tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
      createdAt: editingItem?.createdAt || new Date().toISOString(),
    };

    try {
      setLoading(true);
      
      if (editingItem) {
        // Update existing item via API
        await updateMenuItem(editingItem.id, menuItem);
        showToast('Menu item updated successfully!', 'success');
      } else {
        // Add new item via API
        await createMenuItem(menuItem);
        showToast('Menu item added successfully!', 'success');
      }

      // Refresh the list
      await fetchMenuItems();
      resetForm();
    } catch (error) {
      console.error('Failed to save menu item:', error);
      showToast('Failed to save menu item. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: MenuItem) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      category: item.category || '',
      price: item.price ? item.price.toString() : '',
      image: item.image || '',
      isVeg: item.isVeg || false,
      isAvailable: item.isAvailable ?? true,
      preparationTime: item.preparationTime ? item.preparationTime.toString() : '',
      tags: item.tags ? item.tags.join(', ') : '',
    });
    setShowForm(true);
  };

  const confirmDelete = (itemId: string) => {
    setDeleteModal({ isOpen: true, itemId });
  };

  const handleDelete = async () => {
    if (!deleteModal.itemId) return;

    try {
      setLoading(true);
      await deleteMenuItem(deleteModal.itemId);
      showToast('Menu item deleted successfully!', 'success');
      await fetchMenuItems();
    } catch (error) {
      console.error('Failed to delete item:', error);
      showToast('Failed to delete menu item. Please try again.', 'error');
    } finally {
      setLoading(false);
      setDeleteModal({ isOpen: false, itemId: null });
    }
  };

  const toggleAvailability = async (itemId: string, currentStatus: boolean) => {
    try {
      await updateMenuItemAvailability(itemId, !currentStatus);
      await fetchMenuItems();
    } catch (error) {
      console.error('Failed to update availability:', error);
      showToast('Failed to update availability. Please try again.', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      price: '',
      image: '',
      isVeg: true,
      isAvailable: true,
      preparationTime: '',
      tags: '',
    });
    setEditingItem(null);
    setShowForm(false);
  };

  const categories = Array.from(new Set(menuItems.map(item => item.category)));

  return (
    <div className="menu-management">
      <div className="menu-management-header">
        <div>
          <h1>Menu Management</h1>
          <p>Add, edit, and manage your menu items</p>
        </div>
        <button className="btn btn-accent" onClick={() => setShowForm(true)}>
          <Plus size={18} />
          Add New Item
        </button>
      </div>

      {/* Add/Edit Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={resetForm}
          >
            <motion.div
              className="menu-form-modal glass-card"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}</h2>
                <button className="close-btn" onClick={resetForm}>
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="menu-form">
                <div className="form-grid">
                  <div className="input-group full-width">
                    <label className="input-label">Item Name *</label>
                    <input
                      type="text"
                      name="name"
                      className="input-field"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Margherita Pizza"
                    />
                  </div>

                  <div className="input-group full-width">
                    <label className="input-label">Description *</label>
                    <textarea
                      name="description"
                      className="input-field"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      rows={3}
                      placeholder="Describe the dish..."
                    />
                  </div>

                  <div className="input-group">
                    <label className="input-label">Category *</label>
                    <input
                      type="text"
                      name="category"
                      className="input-field"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Pizza, Burgers"
                      list="categories"
                    />
                    <datalist id="categories">
                      {categories.map(cat => (
                        <option key={cat} value={cat} />
                      ))}
                    </datalist>
                  </div>

                  <div className="input-group">
                    <label className="input-label">Price (₹) *</label>
                    <input
                      type="number"
                      name="price"
                      className="input-field"
                      value={formData.price}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="0.01"
                      placeholder="299"
                    />
                  </div>

                  <div className="input-group">
                    <label className="input-label">Preparation Time (mins) *</label>
                    <input
                      type="number"
                      name="preparationTime"
                      className="input-field"
                      value={formData.preparationTime}
                      onChange={handleInputChange}
                      required
                      min="1"
                      placeholder="20"
                    />
                  </div>

                  <div className="input-group full-width">
                    <label className="input-label">Image URL *</label>
                    <input
                      type="url"
                      name="image"
                      className="input-field"
                      value={formData.image}
                      onChange={handleInputChange}
                      required
                      placeholder="https://images.unsplash.com/..."
                    />
                    {formData.image && (
                      <div className="image-preview">
                        <img src={formData.image} alt="Preview" />
                      </div>
                    )}
                  </div>

                  <div className="input-group full-width">
                    <label className="input-label">Tags (comma-separated)</label>
                    <input
                      type="text"
                      name="tags"
                      className="input-field"
                      value={formData.tags}
                      onChange={handleInputChange}
                      placeholder="italian, cheese, popular"
                    />
                  </div>

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isVeg"
                        checked={formData.isVeg}
                        onChange={handleInputChange}
                      />
                      <span>Vegetarian</span>
                    </label>
                  </div>

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isAvailable"
                        checked={formData.isAvailable}
                        onChange={handleInputChange}
                      />
                      <span>Available</span>
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-ghost" onClick={resetForm}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    <Save size={18} />
                    {editingItem ? 'Update Item' : 'Add Item'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Menu Items Grid */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading menu items...</p>
        </div>
      ) : menuItems.length === 0 ? (
        <div className="empty-state glass-card">
          <ImageIcon size={64} />
          <h3>No menu items yet</h3>
          <p>Add your first menu item to get started</p>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            <Plus size={18} />
            Add First Item
          </button>
        </div>
      ) : (
        <div className="menu-items-grid">
          {menuItems.map((item) => (
            <motion.div
              key={item.id}
              className="menu-item-card glass-card"
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <div className="item-image-container">
                <img src={item.image} alt={item.name} />
                {!item.isAvailable && (
                  <div className="unavailable-badge">Unavailable</div>
                )}
                <div className="item-type-badge">
                  {item.isVeg ? (
                    <span className="badge badge-veg">
                      <Leaf size={12} /> Veg
                    </span>
                  ) : (
                    <span className="badge badge-non-veg">
                      <Drumstick size={12} /> Non-Veg
                    </span>
                  )}
                </div>
              </div>

              <div className="item-details">
                <h3>{item.name}</h3>
                <p className="item-description">{item.description}</p>
                <div className="item-meta">
                  <span className="category-tag">{item.category}</span>
                  <span className="prep-time">🕐 {item.preparationTime} mins</span>
                </div>
                <div className="item-price">₹{item.price}</div>
              </div>

              <div className="item-actions">
                <button
                  className={`btn btn-sm ${item.isAvailable ? 'btn-ghost' : 'btn-accent'}`}
                  onClick={() => toggleAvailability(item.id, item.isAvailable)}
                >
                  {item.isAvailable ? 'Mark Unavailable' : 'Mark Available'}
                </button>
                <div className="action-buttons">
                  <button
                    className="icon-btn"
                    onClick={() => handleEdit(item)}
                    title="Edit"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    className="icon-btn delete-btn"
                    onClick={() => confirmDelete(item.id)}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, itemId: null })}
        onConfirm={handleDelete}
        title="Delete Menu Item"
        message="Are you sure you want to delete this menu item? This action cannot be undone."
        confirmText="Delete"
        isDangerous={true}
      />
    </div>
  );
};

export default MenuManagement;
